import React, { Suspense, lazy } from "react";
import {
  withStyles,
  List,
  ListItem,
  ListItemIcon,
  Collapse,
} from "@material-ui/core";
import UserIcon from "@material-ui/icons/AccountCircle";
import { AppLoading } from "../../../loaders/loaders";
import styles from "../moduleViewstyles";
import { withUserProfile } from "../../../userProfile/userProfileContext";
import { withFeatureFlags } from "../../../userProfile/featureFlagsContext";
import { compose } from "recompose";
import { withAppConfig } from "../../../appConfigContext";
import appConstants, { EVENT_TYPES, SESSION_STORAGE_KEYS } from "../../../appConstants";
import ToolTip from "../../common/StyledToolTip";
import ModuleNotFound from "./ModuleNotFound";
import HoldPros from "./Terminal/HoldPros";
import Events from "../../../utilities/EventEmitter";

const Parameters = lazy(() => import("./Optimization/Parameters"));
const AnchorPoint = lazy(() => import("./Optimization/AnchorPoint"));
const SpeedProfiler = lazy(() => import("./Terminal/SpeedProfiler"));
const Waves = lazy(() => import("./Terminal/Waves"));
const TerminalAccess = lazy(() => import("./Terminal/TerminalAccess"));
const FeederTerminal = lazy(() => import("./Terminal/FeederTerminal"));
const AccessibleTerminal = lazy(() => import("./Terminal/AccessibleTerminal"));
const DriverPreferences = lazy(() => import("./Terminal/DriverPreferences"));
const TrailerInventory = lazy(() => import("./Terminal/TrailerInventory"));
const KPIGaols = lazy(() => import("./Terminal/KPIGoals"));
const ScenarioExplorer = lazy(() => import("./Terminal/ScenarioExplorer"));
const ImportFiles = lazy(() => import("./Terminal/ImportFiles"));
const Display = lazy(() => import("./User/Display"));
const Profile = lazy(() => import("./User/Profile"));
const Notifications = lazy(() => import("./User/Notifications"));
const RouteEquipmentPreference = lazy(() =>
  import("./Terminal/RouteEquipmentPreference")
);
const RouteMappingTool = lazy(() => import("./Terminal/RouteMappingTool"));
const HolidayManagement = lazy(() => import("./HolidayManagement/index"));
const TerminalOnboarding = lazy(() => import("./Terminal/TerminalOnboarding"));
const DriverPreferencesWithUpdate = lazy(() =>
  import("./Terminal/DriverPrefrenceWithUpdate")
);
const EquipmentMoveTerminals = lazy(()=>import("./Terminal/EquipmentMoveTerminal"));

const DEFAULT_SETTING_MODULE = appConstants.SETTINGS_MODULES.PARAMETERS;

class Settings extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeModule:
        sessionStorage.getItem(SESSION_STORAGE_KEYS.ACTIVE_SETTINGS_MODULE) || DEFAULT_SETTING_MODULE,
    };
    this.showSpeedProfiler = this.props.featureFlags.isFeatureEnabled(
      appConstants.features.EnableSpeedProfiler
    );
    this.showRouteEquipmentPreference =
      this.props.featureFlags.isFeatureEnabled(
        appConstants.features.EnableRouteEquipmentPreference
      );
  }

  componentDidMount() {
    Events.on(EVENT_TYPES.SETTINGS.MODULE_CHANGE, this.handleModuleChange);
  }

  componentWillUnmount() {
    Events.off(EVENT_TYPES.SETTINGS.MODULE_CHANGE, this.handleModuleChange);
  }
  
  handleModuleChange = (activeModule) => {
    const isValidModule = Object.values(appConstants.SETTINGS_MODULES).includes(activeModule);
    if(!isValidModule){
      return;
    }
    this.setState({
      activeModule,
    });
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ACTIVE_SETTINGS_MODULE, activeModule);
  }

  onModuleChange = (event) => {
    const activeModule = event.target.getAttribute("data-value");
    if (activeModule) {
      this.setState({
        activeModule,
      });
      sessionStorage.setItem(SESSION_STORAGE_KEYS.ACTIVE_SETTINGS_MODULE, activeModule);
    }
  };

  handleDefaultActiveModule = () => {
    const activeModule = DEFAULT_SETTING_MODULE;
    this.setState({
      activeModule,
    })
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ACTIVE_SETTINGS_MODULE, activeModule);
  }

  getDefaultActiveModule = () => {
    this.handleDefaultActiveModule();
    return <Parameters />;
  }

  getSubComponentModule = (activeModule) => {
    const {
      terminalAccessLabel,
      showRouteMapping,
      enableAccessibleTerminal,
      enableGroupedDriverPreferences,
      enabledEquipmentUpdate,
      enableImportFiles,
      enableHoldPros,
    } = this.props.configJSON;
    const enableTerminalOnboarding = this.props.configJSON.terminalOnboarding?.enableTerminalOnboarding ?? false;
    switch (activeModule) {
      case appConstants.SETTINGS_MODULES.PROFILE:
        return <Profile />;
      case appConstants.SETTINGS_MODULES.DISPLAY:
        return <Display />;
      case appConstants.SETTINGS_MODULES.WAVES:
        return <Waves />;
      case appConstants.SETTINGS_MODULES.TERMINAL_ACCESS:
        return <TerminalAccess terminalAccessLabel={terminalAccessLabel} />;
      case appConstants.SETTINGS_MODULES.FEEDER_TERMINAL:
        return <FeederTerminal shouldReturnNonTerminalLocation/>;        
      case appConstants.SETTINGS_MODULES.ACCESSIBLE_TERMINAL:
        if (enableAccessibleTerminal) {
          return <AccessibleTerminal glEventHub={this.props.glEventHub} />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.SPEED_PROFILER:
        if (this.showSpeedProfiler) {
          return <SpeedProfiler />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.TRAILER_INVENTORY:
        return <TrailerInventory />;
      case appConstants.SETTINGS_MODULES.PARAMETERS:
        return <Parameters />;
      case appConstants.SETTINGS_MODULES.SCENARIO_EXPLORER:
        return <ScenarioExplorer />;
      case appConstants.SETTINGS_MODULES.IMPORT_FILES:
        if (enableImportFiles) {
          return <ImportFiles />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.ANCHOR_POINT:
        return <AnchorPoint />;
      case appConstants.SETTINGS_MODULES.KPI_GOALS:
        return <KPIGaols />;
      case appConstants.SETTINGS_MODULES.NOTIFICATIONS:
        return <Notifications />;
      case appConstants.SETTINGS_MODULES.DRIVER_PREFERENCES:
        return enableGroupedDriverPreferences ? (
          <DriverPreferencesWithUpdate />
        ) : (
          <DriverPreferences />
        );
      case appConstants.SETTINGS_MODULES.ROUTE_EQUIPMENT_PREFERENCE:
        if (this.showRouteEquipmentPreference) {
          return  <RouteEquipmentPreference />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.ROUTE_MAPPING_TOOL:
        if (showRouteMapping) {
          return  <RouteMappingTool glEventHub={this.props.glEventHub} />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.HOLIDAY_MANAGEMENT:
        return <HolidayManagement />;
      case appConstants.SETTINGS_MODULES.TERMINAL_ONBOARDING:
        if (enableTerminalOnboarding) {
          return  <TerminalOnboarding />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.EQUIPMENT_MOVE_TERMINALS:
        if (enabledEquipmentUpdate) {
          return <EquipmentMoveTerminals glEventHub={this.props.glEventHub} />;
        }
        return this.getDefaultActiveModule();
      case appConstants.SETTINGS_MODULES.HOLD_PROS:
        if (enableHoldPros) {
          return <HoldPros />;
        }
        return this.getDefaultActiveModule();
      default:
        return <ModuleNotFound />;
    }
  };

  render() {
    const { classes } = this.props;
    const { activeModule } = this.state;
    const {
      terminalAccessLabel,
      showRouteMapping,
      enableAccessibleTerminal,
      enabledEquipmentUpdate,
      enableImportFiles,
      enableHoldPros,
    } = this.props.configJSON;
    const enableTerminalOnboarding = this.props.configJSON.terminalOnboarding?.enableTerminalOnboarding ?? false;
    const { isAdmin: isAdminUser } =
      (this.props.userData && this.props.userData.userProfile) || {};
    return (
      <div className={classes.settingsContainer}>
        <header
          className={classes.settingsModuleHeader}
          data-automationid="settings-title"
        >
          Settings
        </header>
        <div className={classes.settingsModuleContent}>
          <div className={classes.leftSection}>
            <List onClick={this.onModuleChange}>
              <ListItem
                classes={{
                  root: `${classes.listItemRoot} ${classes.listGroup}`,
                }}
              >
                <ListItemIcon classes={{ root: classes.iconRoot }}>
                  <UserIcon />
                </ListItemIcon>
                User
              </ListItem>
              <Collapse
                in={true}
                classes={{ container: classes.collapseContainer }}
              >
                <List classes={{ root: classes.innerListRoot }} component="nav">
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.PROFILE}
                    selected={activeModule === appConstants.SETTINGS_MODULES.PROFILE}
                  >
                    Profile
                  </ListItem>
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.DISPLAY}
                    selected={activeModule === appConstants.SETTINGS_MODULES.DISPLAY}
                  >
                    Display
                  </ListItem>
                  {isAdminUser && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.NOTIFICATIONS}
                      selected={activeModule === appConstants.SETTINGS_MODULES.NOTIFICATIONS}
                    >
                      Notifications
                    </ListItem>
                  )}
                </List>
              </Collapse>
              <ListItem
                classes={{
                  root: `${classes.listItemRoot} ${classes.listGroup}`,
                }}
              >
                <ListItemIcon classes={{ root: classes.iconRoot }}>
                  <i className="icon-terminal-2"></i>
                </ListItemIcon>
                {terminalAccessLabel.fullName}
              </ListItem>
              <Collapse
                in={true}
                classes={{ container: classes.collapseContainer }}
              >
                <List classes={{ root: classes.innerListRoot }} component="nav">
                  {isAdminUser && showRouteMapping ? (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.ROUTE_MAPPING_TOOL}
                      selected={activeModule === appConstants.SETTINGS_MODULES.ROUTE_MAPPING_TOOL}
                    >
                      Route Mapping Tool
                    </ListItem>
                  ) : null}

                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.WAVES}
                    selected={activeModule === appConstants.SETTINGS_MODULES.WAVES}
                  >
                    Waves
                  </ListItem>
                  {isAdminUser && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.TERMINAL_ACCESS}
                      selected={activeModule === appConstants.SETTINGS_MODULES.TERMINAL_ACCESS}
                    >
                      {terminalAccessLabel.menuLabel}
                    </ListItem>
                  )}
                  {enableAccessibleTerminal && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.ACCESSIBLE_TERMINAL}
                      selected={activeModule === appConstants.SETTINGS_MODULES.ACCESSIBLE_TERMINAL}
                    >
                      Pickup Accessible Terminals
                    </ListItem>
                  )}
                  <ToolTip
                    title={
                      this.props.configJSON.enableFeederTerminal
                        ? "Feeder Terminals"
                        : "Feeder Terminals (Disabled)"
                    }
                  >
                    <div>
                      <ListItem
                        button
                        classes={{
                          root: classes.listItemRoot,
                          selected: classes.listItemSelected,
                        }}
                        data-value={appConstants.SETTINGS_MODULES.FEEDER_TERMINAL}
                        selected={activeModule === appConstants.SETTINGS_MODULES.FEEDER_TERMINAL}
                        disabled={!this.props.configJSON.enableFeederTerminal}
                      >
                        Feeder Terminals
                      </ListItem>
                    </div>
                  </ToolTip>
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.DRIVER_PREFERENCES}
                    selected={activeModule === appConstants.SETTINGS_MODULES.DRIVER_PREFERENCES}
                  >
                    Driver Preferences
                  </ListItem>
                  {isAdminUser && enableHoldPros && <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.HOLD_PROS}
                    selected={activeModule === appConstants.SETTINGS_MODULES.HOLD_PROS}
                  >
                    Hold Pros
                  </ListItem>}
                  {this.showSpeedProfiler && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.SPEED_PROFILER}
                      selected={activeModule === appConstants.SETTINGS_MODULES.SPEED_PROFILER}
                    >
                      Speed Profiler
                    </ListItem>
                  )}
                  {this.showRouteEquipmentPreference && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.ROUTE_EQUIPMENT_PREFERENCE}
                      selected={activeModule === appConstants.SETTINGS_MODULES.ROUTE_EQUIPMENT_PREFERENCE}
                    >
                      Route-Equipment Mapping
                    </ListItem>
                  )}
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.TRAILER_INVENTORY}
                    selected={activeModule === appConstants.SETTINGS_MODULES.TRAILER_INVENTORY}
                  >
                    Trailer Inventory
                  </ListItem>
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.KPI_GOALS}
                    selected={activeModule === appConstants.SETTINGS_MODULES.KPI_GOALS}
                  >
                    KPI Goals
                  </ListItem>
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.SCENARIO_EXPLORER}
                    selected={activeModule === appConstants.SETTINGS_MODULES.SCENARIO_EXPLORER}
                  >
                    Scenario Explorer
                  </ListItem>
                  {isAdminUser && enableTerminalOnboarding && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.TERMINAL_ONBOARDING}
                      selected={activeModule === appConstants.SETTINGS_MODULES.TERMINAL_ONBOARDING}
                    >
                      Terminal Onboarding
                    </ListItem>
                  )}
                  {enableImportFiles && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected,
                      }}
                      data-value={appConstants.SETTINGS_MODULES.IMPORT_FILES}
                      selected={activeModule === appConstants.SETTINGS_MODULES.IMPORT_FILES}
                    >
                      Import Files
                    </ListItem>
                  )}
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.HOLIDAY_MANAGEMENT}
                    selected={activeModule === appConstants.SETTINGS_MODULES.HOLIDAY_MANAGEMENT}
                  >
                   Holiday Management
                  </ListItem>
                  {enabledEquipmentUpdate && (
                    <ListItem
                      button
                      classes={{
                        root: classes.listItemRoot,
                        selected: classes.listItemSelected
                      }}
                      data-value={appConstants.SETTINGS_MODULES.EQUIPMENT_MOVE_TERMINALS}
                      selected={activeModule === appConstants.SETTINGS_MODULES.EQUIPMENT_MOVE_TERMINALS}>
                      Equipment Accessible Terminals
                    </ListItem>
                  )}
                </List>
              </Collapse>
              <ListItem
                classes={{
                  root: `${classes.listItemRoot} ${classes.listGroup}`,
                }}
              >
                <ListItemIcon classes={{ root: classes.iconRoot }}>
                  <i className="icon-icon_opportunity"></i>
                </ListItemIcon>
                Optimization
              </ListItem>
              <Collapse
                in={true}
                classes={{ container: classes.collapseContainer }}
              >
                <List classes={{ root: classes.innerListRoot }} component="nav">
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.PARAMETERS}
                    selected={activeModule === appConstants.SETTINGS_MODULES.PARAMETERS}
                  >
                    Parameters
                  </ListItem>
                  <ListItem
                    button
                    classes={{
                      root: classes.listItemRoot,
                      selected: classes.listItemSelected,
                    }}
                    data-value={appConstants.SETTINGS_MODULES.ANCHOR_POINT}
                    selected={activeModule === appConstants.SETTINGS_MODULES.ANCHOR_POINT}
                  >
                    Anchor Point
                  </ListItem>
                </List>
              </Collapse>
            </List>
          </div>
          <div className={classes.rightSection}>
            <Suspense fallback={<AppLoading />}>
              {this.getSubComponentModule(activeModule)}
            </Suspense>
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  withStyles(styles),
  withFeatureFlags,
  withUserProfile,
  withAppConfig
)(Settings);
